import useSceneStore from '../store/useSceneStore';
import useFurnitureStore from '../store/useFurnitureStore';
import useLogoStore from '../store/useLogoStore';
import useBackgroundStore from '../store/useBackgroundStore';
import useShapeStore from '../store/useShapeStore';
import useTextStore from '../store/useTextStore';
import useTableStore from '../store/useTableStore';
import useLayerStore from '../store/useLayerStore';
import useFloorsStore from '../store/useFloorsStore';
import { dataFetch, uploadDataUrl } from '../api';

async function uploadEmbeddedImages(value, path = 'project-asset') {
  if (typeof value === 'string') {
    if (!value.startsWith('data:image/')) return value;

    const extension = value.match(/^data:image\/([^;,]+)/i)?.[1] || 'png';
    return uploadDataUrl(
      value,
      `${path.replace(/[^a-zA-Z0-9._-]/g, '-')}.${extension}`
    );
  }

  if (Array.isArray(value)) {
    return Promise.all(
      value.map((item, index) => uploadEmbeddedImages(item, `${path}-${index}`))
    );
  }

  if (!value || typeof value !== 'object') return value;

  const entries = await Promise.all(
    Object.entries(value).map(async ([key, item]) => [
      key,
      await uploadEmbeddedImages(item, `${path}-${key}`)
    ])
  );

  return Object.fromEntries(entries);
}

function removeEmbeddedImages(value) {
  if (typeof value === 'string') {
    return value.startsWith('data:image/') ? undefined : value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => removeEmbeddedImages(item));
  }

  if (!value || typeof value !== 'object') return value;

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, item]) => !(
        typeof item === 'string' && item.startsWith('data:image/')
      ))
      .map(([key, item]) => [key, removeEmbeddedImages(item)])
  );
}

function getPersistedImageValue(value) {
  return typeof value === 'string' && value.startsWith('data:image/')
    ? ''
    : value || '';
}

class ProjectManager {
  getOwnerId() {
    return localStorage.getItem('user_id');
  }

  getProjectState() {
    const sceneState = useSceneStore.getState();
    const floorStore = useFloorsStore.getState();
    const currentState = floorStore.captureCurrentState();

    const currentFloors = floorStore.floors.length > 0
      ? floorStore.floors.map((floor) => {
        if (floor.id === floorStore.activeFloorId) {
          return {
            ...floor,
            state: JSON.parse(JSON.stringify(currentState))
          };
        }
        return JSON.parse(JSON.stringify(floor));
      })
      : [];

    return {
      meta: {
        version: '1.2.1',
        timestamp: Date.now(),
        projectName: sceneState.projectName || 'Bez nazwy',
        projectId: sceneState.projectId || null
      },
      floors: currentFloors,
      activeFloorId: floorStore.activeFloorId,
      scene: currentState.scene,
      furniture: currentState.furniture.furnitureItems,
      logos: currentState.logo.logoItems,
      background: currentState.background.backgroundItem,
      shapes: currentState.shape.shapeItems,
      texts: currentState.text.textItems,
      tables: currentState.table.tables,
      layers: currentState.layer.layers
    };
  }

  isMissingRecordError(response) {
    const message = response?.error ?? '';
    return response?.httpStatus === 404 || /record\s+not\s+found/i.test(message);
  }

  async createProject(userId, payload) {
    return dataFetch(
      `create/projects?owner_id=${encodeURIComponent(userId)}`,
      {
        method: 'POST',
        body: JSON.stringify(payload)
      }
    );
  }

  createVersionState(state, projectId) {
    const versionState = JSON.parse(JSON.stringify(state));

    versionState.meta = {
      ...(versionState.meta ?? {}),
      projectId
    };

    versionState.scene = {
      ...(versionState.scene ?? {}),
      projectId
    };

    if (Array.isArray(versionState.floors)) {
      versionState.floors = versionState.floors.map((floor) => ({
        ...floor,
        state: {
          ...(floor?.state ?? {}),
          scene: {
            ...(floor?.state?.scene ?? {}),
            projectId
          }
        }
      }));
    }

    return versionState;
  }

  async saveProjectVersion(state, projectId, userId, thumbnailUrl, timestamp) {
    const versionState = this.createVersionState(state, projectId);

    return dataFetch(
      `create/project_versions?owner_id=${encodeURIComponent(userId)}`,
      {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId,
          project_id: projectId,
          name: versionState?.meta?.projectName ?? 'Bez nazwy',
          thumbnail: getPersistedImageValue(thumbnailUrl),
          data: JSON.stringify(versionState),
          saved_at: timestamp
        })
      }
    );
  }

  async saveToDatabase(thumbnailUrl) {
    const userId = this.getOwnerId();

    if (!userId) {
      return { success: false, error: 'Sesja wygasła. Zaloguj się ponownie.' };
    }

    const sceneStore = useSceneStore.getState();
    const rawState = this.getProjectState();

    let state;
    try {
      state = removeEmbeddedImages(rawState);
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Nie udało się wysłać obrazów projektu.'
      };
    }

    const timestamp = Date.now();
    const payload = {
      user_id: userId,
      name: state.meta.projectName,
      thumbnail: getPersistedImageValue(thumbnailUrl),
      data: JSON.stringify(state),
      updated_at: timestamp
    };
    const projectId = sceneStore.projectId;

    let response = projectId
      ? await dataFetch(
        `update/projects/${projectId}?owner_id=${encodeURIComponent(userId)}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload)
        }
      )
      : await this.createProject(userId, payload);

    if (projectId && this.isMissingRecordError(response)) {
      response = await this.createProject(userId, payload);
    }

    if (response?.status !== 'success') {
      return {
        success: false,
        error: response?.error || 'Nie udało się zapisać projektu w bazie danych.'
      };
    }

    const databaseId = response?.data?.id ?? projectId;

    if (databaseId !== null && databaseId !== undefined) {
      useSceneStore.getState().setProjectId(databaseId);

      const versionResponse = await this.saveProjectVersion(
        state,
        databaseId,
        userId,
        thumbnailUrl,
        timestamp
      );

      if (versionResponse?.status !== 'success') {
        console.error('Save project version failed', versionResponse?.error);
      }
    }

    return { success: true, id: databaseId };
  }

  async getProjects() {
    const userId = this.getOwnerId();

    if (!userId) {
      return { success: false, data: [], error: 'Sesja wygasła. Zaloguj się ponownie.' };
    }

    const response = await dataFetch(
      `read/projects?owner_id=${encodeURIComponent(userId)}&sort=updated_at&order=desc`,
      { method: 'GET' }
    );

    if (response?.status !== 'success') {
      return {
        success: false,
        data: [],
        error: response?.error || 'Nie udało się pobrać projektów.'
      };
    }

    const projects = Array.isArray(response?.data)
      ? response.data.map((project) => ({
        id: project?.id,
        name: project?.name ?? 'Bez nazwy',
        thumbnail: project?.thumbnail ?? '',
        updatedAt: project?.updated_at ?? 0,
        data: project?.data ?? ''
      }))
      : [];

    return { success: true, data: projects };
  }

  async getProjectHistory(projectId) {
    const userId = this.getOwnerId();

    if (!userId || projectId === null || projectId === undefined) {
      return { success: false, data: [], error: 'Sesja wygasła. Zaloguj się ponownie.' };
    }

    const response = await dataFetch(
      `read/project_versions?owner_id=${encodeURIComponent(userId)}&project_id=${encodeURIComponent(projectId)}&sort=saved_at&order=desc&limit=10`,
      { method: 'GET' }
    );

    if (response?.status !== 'success') {
      return {
        success: false,
        data: [],
        error: response?.error || 'Nie udało się pobrać historii projektu.'
      };
    }

    const versions = Array.isArray(response?.data)
      ? response.data.map((version) => ({
        id: version?.id,
        timestamp: version?.saved_at ?? 0,
        thumbnail: version?.thumbnail ?? '',
        data: version?.data ?? ''
      }))
      : [];

    return { success: true, data: versions };
  }

  async getProjectRecord(id, userId) {
    const response = await dataFetch(
      `read/projects/${id}?owner_id=${encodeURIComponent(userId)}`,
      { method: 'GET' }
    );

    if (response?.status === 'success' && response?.data) {
      return response;
    }

    const listResponse = await dataFetch(
      `read/projects?owner_id=${encodeURIComponent(userId)}&sort=updated_at&order=desc`,
      { method: 'GET' }
    );

    if (listResponse?.status !== 'success') {
      return listResponse;
    }

    const project = (listResponse?.data ?? []).find(
      (item) => String(item?.id) === String(id)
    );

    if (!project) {
      return response;
    }

    return {
      ...listResponse,
      data: project
    };
  }

  async loadProjectFromDatabase(id) {
    const userId = this.getOwnerId();

    if (!userId || id === null || id === undefined) return false;

    const response = await this.getProjectRecord(id, userId);

    if (response?.status !== 'success' || !response?.data?.data) {
      return false;
    }

    try {
      const serializedData = typeof response.data.data === 'string'
        ? response.data.data
        : JSON.stringify(response.data.data);
      const state = JSON.parse(serializedData);
      const projectName = response?.data?.name ?? state?.meta?.projectName ?? 'Bez nazwy';

      state.meta = {
        ...(state.meta ?? {}),
        projectName,
        projectId: id
      };

      state.scene = {
        ...(state.scene ?? {}),
        projectName,
        projectId: id
      };

      if (Array.isArray(state.floors)) {
        state.floors = state.floors.map((floor) => ({
          ...floor,
          state: {
            ...(floor?.state ?? {}),
            scene: {
              ...(floor?.state?.scene ?? {}),
              projectName,
              projectId: id
            }
          }
        }));
      }

      this.loadState(state);
      useSceneStore.getState().setProjectName(projectName);
      useSceneStore.getState().setProjectId(id);
      return true;
    } catch (error) {
      console.error('Load project failed', error);
      return false;
    }
  }

  async duplicateProject(id) {
    const userId = this.getOwnerId();

    if (!userId || id === null || id === undefined) return false;

    const response = await dataFetch(
      `read/projects/${id}?owner_id=${encodeURIComponent(userId)}`,
      { method: 'GET' }
    );

    if (response?.status !== 'success' || !response?.data?.data) {
      return false;
    }

    try {
      const source = response.data;
      const duplicateState = JSON.parse(source.data);

      duplicateState.meta = {
        ...duplicateState.meta,
        projectId: null,
        projectName: `${source?.name ?? 'Bez nazwy'} (kopia)`,
        timestamp: Date.now()
      };

      const createResponse = await this.createProject(userId, {
        user_id: userId,
        name: duplicateState.meta.projectName,
        thumbnail: getPersistedImageValue(source?.thumbnail),
        data: JSON.stringify(duplicateState),
        updated_at: Date.now()
      });

      return createResponse?.status === 'success';
    } catch (error) {
      console.error('Duplicate project failed', error);
      return false;
    }
  }

  async deleteProject(id) {
    const userId = this.getOwnerId();

    if (!userId || id === null || id === undefined) return false;

    const response = await dataFetch(
      `delete/projects/${id}?owner_id=${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );

    if (response?.status !== 'success') {
      return false;
    }

    if (useSceneStore.getState().projectId === id) {
      useSceneStore.getState().setProjectId(null);
    }

    return true;
  }

  loadState(data) {
    if (!data) return;

    try {
      const stateData = JSON.parse(JSON.stringify(data));
      const projectId = stateData?.meta?.projectId ?? stateData?.scene?.projectId ?? null;

      if (projectId !== null) {
        useSceneStore.getState().setProjectId(projectId);
      }

      if (stateData?.meta?.projectName || stateData?.scene?.projectName) {
        useSceneStore.getState().setProjectName(
          stateData?.meta?.projectName ?? stateData?.scene?.projectName
        );
      }

      const validFloors = stateData?.floors?.filter(
        (floor) => floor?.state && typeof floor.state === 'object'
      ) ?? [];

      if (validFloors.length > 0) {
        useFloorsStore.getState().loadMultiFloorProject(
          validFloors,
          stateData.activeFloorId
        );
        return;
      }

      const legacyState = {
        scene: stateData.scene || {},
        furniture: { furnitureItems: stateData.furniture || stateData.furnitureItems || {} },
        logo: { logoItems: stateData.logos || {} },
        background: { backgroundItem: stateData.background || null },
        shapes: { shapeItems: stateData.shapes || {} },
        text: { textItems: stateData.texts || {} },
        table: { tables: stateData.tables || {} },
        layer: { layers: stateData.layers || {} }
      };

      useFloorsStore.getState().applyState(legacyState);
      useFloorsStore.getState().resetWithProject(legacyState);
    } catch (error) {
      console.error('Load state failed', error);
    }
  }

  newProject() {
    useSceneStore.getState().resetScene();
    useSceneStore.setState({
      projectName: 'Nowy Projekt',
      projectId: null,
      scale: 1,
      position: { x: 0, y: 0 }
    });
    useFurnitureStore.getState().clearAllFurniture();
    useLogoStore.getState().clearAllLogos();
    useBackgroundStore.getState().clearBackground();
    useShapeStore.setState({ shapeItems: {}, selectedShapeId: null });
    useTextStore.setState({ textItems: {}, selectedTextId: null });
    useTableStore.setState({ tables: {}, selectedTableId: null });

    const floorStore = useFloorsStore.getState();
    floorStore.resetWithProject(floorStore.captureCurrentState());
  }

  exportProject() {
    const state = this.getProjectState();
    const blob = new Blob([JSON.stringify(state, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${state.meta.projectName.replace(/\s+/g, '_')}_floorplan.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  async importProject(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target.result);
          this.loadState(json);
          useSceneStore.getState().setProjectId(null);
          resolve(true);
        } catch (error) {
          reject(error);
        }
      };

      reader.readAsText(file);
    });
  }
}

const projectManager = new ProjectManager();

export default projectManager;